import { Button, Typography } from "@mui/material";
import { UseMutationResult } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { NetworkRequestState } from "../../../../../../common/models/NetworkRequest";
import { useDataMapPendingRecipientAssocationCollectionGroupIds } from "../../../../../../common/service/resources/data-map/dataMapPending";
import { CollectionGroupType } from "../../../../../../common/service/server/controller/CollectionGroupsController";
import { CollectionGroupDetailsDto } from "../../../../../../common/service/server/dto/CollectionGroupDto";
import { DataMapDto } from "../../../../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationPublicId, UUIDString } from "../../../../../../common/service/server/Types";
import { PageHeader } from "../../../../../components/layout/header/Header";
import { StandardPageWrapper } from "../../../../../components/layout/StandardPageWrapper";
import { CollectionGroupMultiSelect } from "../../../../../components/organisms/collection-groups/CollectionGroupMultiSelect";
import {
  useOrganizationVendorDetails,
  useOrganizationVendors,
  usePrimaryOrganizationId,
} from "../../../../../hooks";
import {
  EXCLUDE_GROUP_EMPLOYMENT,
  useCollectionGroups,
} from "../../../../../hooks/useCollectionGroups";
import { RouteHelpers, Routes } from "../../../../../root/routes";
import { ProgressFooter, ProgressFooterActions } from "../../../../get-compliant/ProgressFooter";
import { PageHeaderBack } from "../../../../../components/layout/header/PageHeaderBack";
import { DataRecipientLogo } from "../../../../../components/DataRecipientLogo";
import { DeleteForever } from "@mui/icons-material";
import { useDataRecipientSearch } from "../../../../../hooks/useOrganizationVendors";
import { ConfirmDialog } from "../../../../../components/dialog";

export type Params = {
  dataRecipientId: UUIDString;
};

export const CollectionGroupAssociationForDataRecipientPage = () => {
  const history = useHistory();
  const orgId = usePrimaryOrganizationId();
  const { dataRecipientId } = useParams<Params>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const data = useDataRecipientAssociationForGroup(orgId, dataRecipientId, null);

  const [dataRecipient] = useOrganizationVendorDetails(orgId, dataRecipientId);

  const contexts = dataRecipient?.collectionContext ?? [];

  const deleteConfirmCopy =
    contexts.length <= 1 ? (
      "Are you sure you want to delete this data recipient? Any information you've mapped to this data recipient will be lost."
    ) : (
      <>
        Are you sure you want to delete this Data Recipient? Clicking ‘yes’ will remove the Data
        Recipient from both your consumer group{" "}
        <strong>
          <em>and</em>
        </strong>{" "}
        your employment groups. If you have questions, contact{" "}
        <a href="mailto:help@truevault.com">help@truevault.com</a>.
      </>
    );

  const { actions, requests } = useDataRecipientSearch(orgId, {
    onRemoveSuccess: () => {
      setDeleteConfirmOpen(false);
      history.push(Routes.dataMap.root);
    },
  });
  const { removingVendor } = requests;
  const { handleRemoveVendor } = actions;

  return (
    <StandardPageWrapper
      requests={[...Object.values(data.requests)]}
      footer={
        <ProgressFooter
          actions={
            <ProgressFooterActions
              nextOnClick={() =>
                history.push(
                  RouteHelpers.dataMap.dataRecipient.workflow.disclosure(dataRecipientId),
                )
              }
              nextContent="Next"
              prevContent="Cancel"
              nextDisabled={data.mutations.updateAssociation.isLoading || !data.selected?.length}
              prevOnClick={() => history.push(Routes.dataMap.root)}
            />
          }
        />
      }
    >
      <PageHeader
        titleContent={data?.dataRecipient?.name}
        titleHeaderContent={
          <PageHeaderBack to={`${Routes.dataMap.root}?tab=data_recipients`}>
            Back to Data Map
          </PageHeaderBack>
        }
        logo={<DataRecipientLogo dataRecipient={data?.dataRecipient} size={48} />}
        actions={
          <>
            <Button
              onClick={() => setDeleteConfirmOpen(true)}
              color="error"
              startIcon={<DeleteForever />}
              variant="text"
            >
              Remove
            </Button>
          </>
        }
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={() => handleRemoveVendor(dataRecipient)}
        confirmTitle={`Delete ${dataRecipient?.name}?`}
        contentText={deleteConfirmCopy}
        confirmText="Yes"
        confirmLoading={removingVendor.running}
      />

      <Typography>
        Whose data does <strong>{data?.dataRecipient?.name}</strong> process?
      </Typography>

      <CollectionGroupMultiSelect {...data.multiselect} />
    </StandardPageWrapper>
  );
};

type UseDataRecipientAssociationForGroup = {
  selected: UUIDString[];
  pending: UUIDString[];
  dataRecipient: OrganizationDataRecipientDto;
  mutations: {
    updateAssociation: UseMutationResult;
  };
  fetched: {
    orgDataRecipients: OrganizationDataRecipientDto[];
    dataMap: DataMapDto;
    collectionGroups: CollectionGroupDetailsDto[];
  };
  requests: {
    sources: NetworkRequestState;
    dataMap: NetworkRequestState;
    collectionGroups: NetworkRequestState;
  };
  multiselect: {
    collectionGroups: CollectionGroupDetailsDto[];
    selected: UUIDString[];
    pending: UUIDString[];
    onChange: (args: { groupId: UUIDString; included: boolean }) => void;
  };
};

export const useDataRecipientAssociationForGroup = (
  orgId: OrganizationPublicId,
  dataRecipientId: UUIDString,
  excludeTypes: CollectionGroupType[] = EXCLUDE_GROUP_EMPLOYMENT,
): UseDataRecipientAssociationForGroup => {
  const [collectionGroups, groupsRequest] = useCollectionGroups(orgId, undefined, excludeTypes);
  const [orgDataRecipients, orgDataRecipientsRequest] = useOrganizationVendors(orgId);
  const {
    data: dataMap,
    request: dataMapRequest,
    mutators: { patch },
  } = useDataMap(orgId);

  const dataRecipient = useMemo(
    () => orgDataRecipients?.find((s) => s.vendorId === dataRecipientId),
    [orgDataRecipients, dataRecipientId],
  );

  const onChange = ({ groupId, included }) => {
    patch.mutate({
      operations: [
        {
          target: "RECIPIENT_ASSOCIATION",
          action: included ? "ADD" : "REMOVE",
          groupId: groupId,
          vendorId: dataRecipientId,
        },
      ],
    });
  };

  const selected = useMemo(
    () =>
      dataMap.recipientAssociations
        .filter((a) => a.vendorId == dataRecipientId)
        .map((a) => a.groupId),
    [dataMap.recipientAssociations, dataRecipientId],
  );

  const pending = useDataMapPendingRecipientAssocationCollectionGroupIds(dataMap, {
    vendorId: dataRecipientId,
  });

  return {
    selected,
    pending,
    dataRecipient,
    mutations: {
      updateAssociation: patch,
    },
    fetched: {
      orgDataRecipients,
      dataMap,
      collectionGroups,
    },
    requests: {
      sources: orgDataRecipientsRequest,
      dataMap: dataMapRequest,
      collectionGroups: groupsRequest,
    },
    multiselect: {
      collectionGroups,
      selected,
      pending,
      onChange,
    },
  };
};
